import { PoopReport, Settings } from '../types';
import * as firebase from './firebase';

// Check if Firebase is configured
const useFirebase = firebase.isFirebaseConfigured();

// ============ LOCAL STORAGE FALLBACK ============
const STORAGE_KEYS = {
  POOP_REPORTS: 'ptp_poop_reports',
  SETTINGS: 'ptp_settings',
  SEEDED: 'ptp_seeded'
};

const DEFAULT_SETTINGS: Settings = {
  expiryDays: 30,
  blurMeters: 0,
  // Colors
  headerBgColor: '#9a3612',
  headerTextColor: '#ffffff',
  footerBgColor: '#9a3612',
  footerTextColor: '#ffffff',
  // Branding
  logoText: 'Pin the Poo',
  logoFont: 'The Seasons',
  logoFontSize: 1.5,
  logoColor: '#ffffff',
  tagline: 'Keeping our streets clean, one pin at a time',
  // Content
  missionTitle: 'Our Mission',
  missionContent: `**Pin the Poo** is a community-driven initiative to map and track dog waste in our neighbourhoods.

## Why It Matters

Dog fouling is more than just unpleasant—it's a health hazard and affects quality of life for everyone. By mapping problem areas, we can:

- **Identify hotspots** that need attention
- **Track improvements** over time  
- **Encourage responsibility** among dog owners
- **Support local councils** with data for better enforcement

## How It Works

1. **Spot it** — See uncollected dog waste in a public area
2. **Pin it** — Drop a marker on the map at that location
3. **Track it** — Watch as the community maps problem areas

## Get Involved

Every pin helps build a clearer picture of the problem. Together, we can make our streets cleaner and safer for everyone.

*Let's clean up our community, one pin at a time.*`,
  privacyContent: `# Privacy Policy

**Last updated:** January 2026

## What We Collect

When you use Pin the Poo, we collect:

### Information You Provide
- **Location data** — The GPS coordinates where you place pins. These are recorded precisely to help identify problem areas accurately.
- **Timestamp** — When you create a pin.

### Information Collected Automatically
- **Device information** — Browser type, operating system, and device type.
- **IP address** — Your internet protocol address, which may indicate your general location.
- **Usage data** — Pages visited, features used, and interactions with the app.
- **Cookies** — Small files stored on your device to improve your experience and for analytics.

## How We Use Your Information

We use the collected information to:
- Display poop reports on the map
- Analyse usage patterns to improve the app
- Maintain and secure the service
- Generate anonymous, aggregated statistics

## Data Storage

Your data is stored using Firebase (Google Cloud Platform). Data may be processed in various locations where Google operates data centres.

## Data Retention

- **Pin reports** automatically expire after 30 days unless held by an administrator.
- **Analytics data** is retained according to Google Analytics' standard retention policies.

## Your Rights (GDPR)

Under UK and EU data protection law, you have the right to:
- **Access** your personal data
- **Rectification** of inaccurate data
- **Erasure** of your data ("right to be forgotten")
- **Restrict** processing of your data
- **Object** to processing
- **Data portability**

To exercise these rights, contact us using the details below.

## Third-Party Services

We use:
- **Firebase** (Google) — Database and hosting
- **Google Analytics** — Usage analytics

These services have their own privacy policies.

## Children's Privacy

This service is not intended for children under 13. We do not knowingly collect data from children.

## Changes to This Policy

We may update this policy from time to time. Continued use of the app after changes constitutes acceptance.

## Contact Us

For privacy-related questions or to exercise your rights, contact us at the email provided in the app.`,
  termsContent: `# Terms of Use

**Last updated:** January 2026

## Acceptance of Terms

By accessing or using Pin the Poo ("the Service"), you agree to be bound by these Terms of Use.

## Description of Service

Pin the Poo is a community mapping tool that allows users to report locations of dog waste in public areas.

## User Conduct

You agree NOT to:
- Submit false or misleading reports
- Use the Service to harass, stalk, or identify individuals
- Attempt to access restricted areas or admin functions
- Interfere with the proper functioning of the Service
- Use the Service for any unlawful purpose
- Submit reports on private property without permission

## Content

### Your Content
By submitting pins/reports, you grant us a non-exclusive, royalty-free licence to use, display, and distribute that content as part of the Service.

### Accuracy
We do not guarantee the accuracy of user-submitted reports. Use the information at your own discretion.

## Intellectual Property

The Service, including its design, code, and branding, is owned by us and protected by intellectual property laws.

## Disclaimer of Warranties

THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.

We do not warrant that:
- The Service will be uninterrupted or error-free
- Reports are accurate or current
- The Service will meet your requirements

## Limitation of Liability

TO THE FULLEST EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.

## Indemnification

You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the Service or violation of these Terms.

## Modifications

We reserve the right to modify these Terms at any time. Continued use after changes constitutes acceptance.

## Termination

We may terminate or suspend access to the Service immediately, without notice, for conduct that violates these Terms.

## Governing Law

These Terms shall be governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.

## Severability

If any provision of these Terms is found unenforceable, the remaining provisions will continue in effect.

## Contact

For questions about these Terms, contact us at the email provided in the app.`,
  // Contact
  contactEmail: 'hello@pinthepoo.com',
  contactName: 'Pin the Poo Team'
};

// No seed reports - start fresh
const SEED_REPORTS: PoopReport[] = [];

function initializeLocalStorage(): void {
  const seeded = localStorage.getItem(STORAGE_KEYS.SEEDED);
  if (!seeded) {
    localStorage.setItem(STORAGE_KEYS.POOP_REPORTS, JSON.stringify(SEED_REPORTS));
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(DEFAULT_SETTINGS));
    localStorage.setItem(STORAGE_KEYS.SEEDED, 'true');
  }
}

// Local storage functions
function getLocalPoopReports(): PoopReport[] {
  initializeLocalStorage();
  try {
    const data = localStorage.getItem(STORAGE_KEYS.POOP_REPORTS);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function addLocalPoopReport(report: PoopReport): void {
  const reports = getLocalPoopReports();
  reports.push(report);
  localStorage.setItem(STORAGE_KEYS.POOP_REPORTS, JSON.stringify(reports));
}

function updateLocalPoopReport(id: string, updates: Partial<PoopReport>): void {
  const reports = getLocalPoopReports();
  const index = reports.findIndex(r => r.id === id);
  if (index >= 0) {
    reports[index] = { ...reports[index], ...updates };
    localStorage.setItem(STORAGE_KEYS.POOP_REPORTS, JSON.stringify(reports));
  }
}

function deleteLocalPoopReport(id: string): void {
  const reports = getLocalPoopReports().filter(r => r.id !== id);
  localStorage.setItem(STORAGE_KEYS.POOP_REPORTS, JSON.stringify(reports));
}

function getLocalSettings(): Settings {
  initializeLocalStorage();
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    return data ? { ...DEFAULT_SETTINGS, ...JSON.parse(data) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function updateLocalSettings(updates: Partial<Settings>): void {
  const settings = getLocalSettings();
  localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify({ ...settings, ...updates }));
}

// ============ EXPORTED FUNCTIONS (Firebase or Local) ============

// POOP REPORTS
export async function getPoopReports(): Promise<PoopReport[]> {
  if (useFirebase) {
    return firebase.getPoopReports();
  }
  return getLocalPoopReports();
}

export function subscribeToPoopReports(callback: (reports: PoopReport[]) => void): () => void {
  if (useFirebase) {
    return firebase.subscribeToPoopReports(callback);
  }
  // For local storage, just call once (no real-time updates)
  callback(getLocalPoopReports());
  return () => {};
}

export async function addPoopReport(report: PoopReport): Promise<void> {
  if (useFirebase) {
    const { id, ...reportData } = report;
    await firebase.addPoopReport(reportData);
  } else {
    addLocalPoopReport(report);
  }
}

export async function updatePoopReport(id: string, updates: Partial<PoopReport>): Promise<void> {
  if (useFirebase) {
    await firebase.updatePoopReport(id, updates);
  } else {
    updateLocalPoopReport(id, updates);
  }
}

export async function deletePoopReport(id: string): Promise<void> {
  if (useFirebase) {
    await firebase.deletePoopReport(id);
  } else {
    deleteLocalPoopReport(id);
  }
}

// SETTINGS
export async function getSettings(): Promise<Settings> {
  if (useFirebase) {
    return firebase.getSettings();
  }
  return getLocalSettings();
}

export function subscribeToSettings(callback: (settings: Settings) => void): () => void {
  if (useFirebase) {
    return firebase.subscribeToSettings(callback);
  }
  // For local storage, just call once
  callback(getLocalSettings());
  return () => {};
}

export async function updateSettings(updates: Partial<Settings>): Promise<void> {
  if (useFirebase) {
    await firebase.updateSettings(updates);
  } else {
    updateLocalSettings(updates);
  }
}

// RESET
export function resetAllData(): void {
  localStorage.removeItem(STORAGE_KEYS.POOP_REPORTS);
  localStorage.removeItem(STORAGE_KEYS.SETTINGS);
  localStorage.removeItem(STORAGE_KEYS.SEEDED);
  initializeLocalStorage();
}

// Export for checking mode
export { useFirebase, DEFAULT_SETTINGS };
